import React, { Component, Fragment, createRef, cloneElement } from 'react';
import {
  array,
  bool,
  func,
  node,
  string,
  object,
  number,
  oneOfType,
} from 'prop-types';
import CarrotToggle from 'library/components/carrot-toggle';
import * as StyledDropdown from 'library/components/form-components/dropdown/styled-dropdown';
import MultiSelectOption from 'library/components/form-components/dropdown/components/multi-select-option';
import BaseOption from 'library/components/form-components/dropdown/components/base-option';
import KeyCodes from 'library/helpers/key-codes';
import 'library/helpers/array';
import SavedNotification from 'library/components/form-components/saved-notification';

/**
 * children MUST be a single array and if there is only 1 child (a hard-coded component)
 * then you must wrap that component in an arary
 */
class Dropdown extends Component {
  constructor(props) {
    super(props);
    this.dropdownRef = createRef();
    this.titleRef = createRef();
  }

  static defaultProps = {
    compact: false,
    carrot: true,
    description: '',
    icon: null,
    isOpen: false,
    options: null,
    placeholderOnly: false,
    placeholder: '',
    scrollOutOffsetTop: 0,
    persistOpen: false,
    iconProps: {},
  };

  static propTypes = {
    icon: node,
    label: string,
    ariaLabel: string,
    options: array,
    multiselect: bool,
    name: string,
    onChange: func,
    isOpen: bool,
    children: node,
    className: string,
    placeholder: string,
    compact: bool,
    carrot: bool,
    placeholderOnly: bool,
    description: oneOfType([object, string]),
    contentClassName: string,
    scrollOutOffsetTop: number,
    persistOpen: bool,
    iconProps: object,
  };

  state = {
    active: false,
    isOpen: false,
    value: '',
    selectedOptions: [],
  };

  componentDidMount = () => {
    if (this.props.isOpen) {
      this.handleClick();
    }
  };

  /**
   *
   * @param {OnKeyPressEvent} e
   *
   * Toggles the dropdown selection option's state. Relies on the onKeyPress
   * event and checks if the key pressed is a "positive" action, then
   * toggles the "isOpen" state for the dropdown list.
   */
  handleActiveKeypress = (e) => {
    e.preventDefault();
    if (this.state.isOpen && KeyCodes.POSITIVE_AFFIRMATIONS.includes(e.which)) {
      this.setState({ isOpen: false });
    }
    if (
      !this.state.isOpen &&
      KeyCodes.POSITIVE_AFFIRMATIONS.includes(e.which)
    ) {
      this.setState({ isOpen: true });
    }
  };

  /**
   *
   * @param {OnKeyPressEvent} e
   *
   * This is the base onKeyPress event passed to the selection options if
   * there was not a provided onKeyPress handler given directly to the
   * dropdown children, OR if selection data was provided directly to the "options"
   * prop. Will pass the event data (which, target: {key, value}) to the "handleChange"
   * function and refocuses the user on the parent dropdown element
   * if in single-seleciton mode.
   */
  handleKeyPress = (e) => {
    if (this.state.isOpen && KeyCodes.POSITIVE_AFFIRMATIONS.includes(e.which)) {
      this.handleChange(e);
      this.props.multiselect
        ? null
        : this.setState({ isOpen: false }, this.dropdownRef.current?.focus());
    }
  };

  /**
   *
   * @param {OnChangeEvent} e
   *
   * This is the base onChange event passed to the selection options if there
   * was not a provided onChange handler given directly to the dropdown children,
   * OR if selection data was provided directly to the "options" prop.
   * If the multiselect prop is true merge in the updated values for the given
   * selections, else update the single value.
   */
  handleChange = (e) => {
    const { multiselect, onChange } = this.props;

    let value = e?.target?.value;
    if (!value && typeof value !== 'string')
      value = e.target.getAttribute('value');

    if (multiselect) {
      let newState = [...this.state.selectedOptions.toggleValues(value)];
      this.setState({
        selectedOptions: newState,
        value: newState.length ? value : null,
      });
    } else {
      // store the user-friendly label in state for displaying to user
      this.setState({
        value: e.target.getAttribute('label'),
      });
    }
    if (onChange) onChange(e);
  };

  handleClick = (e) => {
    if (
      !this.props.multiselect ||
      !this.dropdownRef?.current?.contains(e.target) ||
      !this.state.isOpen ||
      e.target === this.dropdownRef?.current ||
      e.target === this.titleRef?.current
    ) {
      this.open(!this.state.isOpen);
    }
  };

  handleScrollOut = (e) => {
    if (
      (!this.dropdownRef?.current?.contains(e.target) ||
        !this.state.isOpen ||
        e.target === this.dropdownRef?.current ||
        e.target === this.titleRef?.current) &&
      this.dropdownRef?.current?.getBoundingClientRect()?.top <=
        this.props.scrollOutOffsetTop
    ) {
      this.open(!this.state.isOpen);
    }
  };

  addRemoveEventListener = () => {
    if (this.state.isOpen) {
      mount.addEventListener('click', this.handleClick);
      if (this.props.scrollOutOffsetTop > 0) {
        // this will close the dropdown
        // when the user scrolls beyond the value of scrollOutOffsetTop pixels
        window.addEventListener('scroll', this.handleScrollOut);
      }
    } else {
      mount.removeEventListener('click', this.handleClick);
      if (this.props.scrollOutOffsetTop > 0) {
        window.removeEventListener('scroll', this.handleScrollOut);
      }
      this.dropdownRef?.current.blur();
    }
  };

  /**
   * If we are using the props.options array and are not rendering custom
   * selection options via { children }, render the provided options via
   * the base Single or Multiselect option components. The base onClick/
   * onKeyPress events are used here.
   *
   */
  mapOptions = () =>
    this.props.options.map((child) =>
      this.props.multiselect ? (
        <MultiSelectOption
          key={child.value}
          label={child.label}
          onClick={this.handleChange}
          onKeyPress={this.handleKeyPress}
          name={this.props.name}
          value={child.value}
          active={this.state.selectedOptions.includes(child.value)}
          className={child.className}
        />
      ) : (
        <BaseOption
          key={child.value}
          name={this.props.name}
          label={child.label}
          onClick={this.handleChange}
          onKeyPress={this.handleKeyPress}
          value={child.value}
          className={child.className}
        />
      )
    );

  // For custom children, set onClick & onKeyPress handlers on provided child
  // nodes from the parent Dropdown component
  // ONLY if they don't already have an onClick/ onKeyPress prop
  mapChildren = () =>
    this.props.children?.length ? (
      this.props.children?.map((child) =>
        cloneElement(child, {
          onClick: child.props?.onClick || this.handleChange,
          onKeyPress: child.props?.onKeyPress || this.handleKeyPress,
        })
      )
    ) : (
      <option>No selection options</option>
    );

  open = (isOpen) => {
    this.setState(
      {
        isOpen,
        active: isOpen,
      },
      this.addRemoveEventListener
    );
  };

  renderChildren = () => {
    return this.state.isOpen || this.props.persistOpen ? (
      <StyledDropdown.StyledDropdownContent
        compact={this.props.compact}
        className={this.props.contentClassName}
        hide={!this.state.isOpen}
      >
        {!!this.props.description && (
          <StyledDropdown.StyledDropdownDescription>
            {this.props.description}
          </StyledDropdown.StyledDropdownDescription>
        )}
        {this.props.options?.length ? this.mapOptions() : this.mapChildren()}
      </StyledDropdown.StyledDropdownContent>
    ) : null;
  };

  renderLabel = () => (
    <Fragment>
      <StyledDropdown.StyledDropdownLabel>
        {this.props.label}
      </StyledDropdown.StyledDropdownLabel>
      {!!this.props.carrot && (
        <StyledDropdown.StyledIcon>
          <CarrotToggle active={this.state.isOpen} />
        </StyledDropdown.StyledIcon>
      )}
    </Fragment>
  );

  /**
   * When rendering a placeholder value, this logic will format it gracefully
   * based on the selection type. (E.G. multiselect => "2 selection(s)" vs.
   * single => "Option 2")
   */
  renderPlaceholderOrValue = () => {
    const { multiselect, placeholder, placeholderOnly } = this.props;
    const { value } = this.state;
    if (placeholderOnly) {
      return placeholder;
    }
    return multiselect && this.state.selectedOptions.length
      ? `${this.state.selectedOptions.length} selection(s)`
      : value || placeholder;
  };

  render = () => {
    const {
      className,
      name,
      label,
      ariaLabel,
      icon,
      multiselect,
      compact,
      placeholderOnly,
      saved,
      iconProps,
      ...props
    } = this.props;
    const { isOpen } = this.state;
    let IconComponent = icon;
    return (
      <StyledDropdown.StyledDropdownContainer
        aria-label={`${
          ariaLabel || label
        } Use space bar or enter to toggle selection options. Currently ${
          isOpen ? `expanded` : `collapsed`
        }. ${
          multiselect ? `` : `Current value is`
        } ${this.renderPlaceholderOrValue()}.`}
        aria-expanded={isOpen}
        ref={this.dropdownRef}
        onClick={this.handleClick}
        onKeyPress={this.handleActiveKeypress}
        tabIndex={0}
        className={`${className}${isOpen ? ' active' : ''}`}
        compact={compact}
      >
        <StyledDropdown.StyledDropdown
          id={name}
          name={name}
          placeholderOnly={placeholderOnly}
        >
          {IconComponent ? <IconComponent {...iconProps} /> : null}
          <span ref={this.titleRef}>{this.renderPlaceholderOrValue()}</span>
          {this.renderChildren()}
        </StyledDropdown.StyledDropdown>
        {this.renderLabel()}
        <SavedNotification saved={saved} />
      </StyledDropdown.StyledDropdownContainer>
    );
  };
}

Dropdown.displayName = 'Dropdown';

export default Dropdown;
